import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TrustCues from "../components/TrustCues";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useSEO } from "../lib/useSEO";
import { api } from "../lib/api";
import { Mail, Phone, Clock, CheckCircle2, MapPin } from "lucide-react";
import { BUSINESS_CONTACT as BC } from "../data/content";
import { toast } from "sonner";

const ROLES = [
  { v: "attorney", l: "Attorney / law firm" },
  { v: "plaintiff", l: "Plaintiff" },
  { v: "paralegal", l: "Paralegal or case manager" },
  { v: "case_owner", l: "Someone with a pending case" },
  { v: "other", l: "Other" },
];
const TOPICS = [
  { v: "pre_settlement", l: "Pre-settlement funding for a client" },
  { v: "litigation_finance", l: "Litigation finance" },
  { v: "attorney_lending", l: "Attorney or law firm lending" },
  { v: "personal_funding", l: "I need funding for my case" },
  { v: "other", l: "Something else" },
];

export default function Contact() {
  useSEO("Contact us", "Get in touch with Instabridge Funding. Plaintiff, attorney, or partner — we'll route your message to the right person and reply within an hour.", { canonical: "https://instabridgefunding.com/contact" });

  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "", topic: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Name, email, and message are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Navbar />

      <section className="bg-[#F9FAFB] border-b border-[#E5E7EB] py-16">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">Contact</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A2540] font-light leading-[1.05]">
            Let's <span className="font-semibold">start a conversation.</span>
          </h1>
          <p className="mt-5 text-lg text-[#6B7280] max-w-2xl">Tell us who you are and what you need. We'll route your message to the right person and reply within an hour.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-6">
            <Info icon={Mail} t="Email" v="hello@instabridgefunding.com" />
            <Info icon={Phone} t="Phone" v={<a href={BC.tel} className="hover:text-[#0066FF] transition-colors" data-testid="contact-phone-link">{BC.phoneDisplay}</a>} />
            <Info icon={MapPin} t="Office" v={<>
              <address className="not-italic" data-testid="contact-address">{BC.street}<br />{BC.city}, {BC.region} {BC.postalCode}</address>
              <a href={BC.mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-block mt-1.5 text-sm text-[#0066FF] hover:underline underline-offset-4" data-testid="contact-directions-link">Get directions ↗</a>
            </>} />
            <Info icon={Clock} t="Response time" v="Usually within 1 hour during business hours" />
            <div className="bg-[#0A2540] text-white rounded-2xl p-6">
              <div className="uppercase-label !text-white/60 mb-3">Need cash now?</div>
              <p className="text-white/80 text-sm leading-relaxed">Skip the form — apply for funding directly. Approval in hours.</p>
              <a href="/apply" className="inline-block mt-4 text-white font-medium hover:text-[#0066FF]">Apply now →</a>
            </div>
          </div>

          <div className="lg:col-span-8">
            {done ? (
              <div className="bg-[#ECFDF5] border border-[#A7F3D0] rounded-2xl p-10 text-center" data-testid="contact-success">
                <CheckCircle2 className="w-12 h-12 text-[#047857] mx-auto" />
                <h2 className="font-display text-2xl font-semibold text-[#0A2540] mt-4">Message received</h2>
                <p className="text-[#374151] mt-2">We'll get back to you within an hour during business hours. Thanks for reaching out.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-[#E5E7EB] rounded-2xl p-7 lg:p-9 space-y-5" data-testid="contact-form">
                <div>
                  <Label htmlFor="role">I am</Label>
                  <Select value={form.role} onValueChange={set("role")}>
                    <SelectTrigger id="role" className="mt-1.5" data-testid="contact-role"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="topic">What can we help with?</Label>
                  <Select value={form.topic} onValueChange={set("topic")}>
                    <SelectTrigger id="topic" className="mt-1.5" data-testid="contact-topic"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{TOPICS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name">Full name</Label>
                    <Input id="name" value={form.name} onChange={set("name")} className="mt-1.5" data-testid="contact-name" required />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={form.email} onChange={set("email")} className="mt-1.5" data-testid="contact-email" required />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input id="phone" value={form.phone} onChange={set("phone")} className="mt-1.5" data-testid="contact-phone" />
                </div>
                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea id="message" rows={5} value={form.message} onChange={set("message")} className="mt-1.5" data-testid="contact-message" required />
                </div>
                <Button type="submit" className="btn-primary-navy w-full h-12 rounded-full" disabled={submitting} data-testid="contact-submit-btn">
                  {submitting ? "Sending…" : "Send message"}
                </Button>
                <TrustCues variant="trust-line" audience="consumer" align="center" testid="contact-trust" />
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Info = ({ icon: Icon, t, v }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 shrink-0 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB] flex items-center justify-center"><Icon className="w-4 h-4 text-[#0066FF]" /></div>
    <div>
      <div className="uppercase-label">{t}</div>
      <div className="text-[#0A2540] mt-1">{v}</div>
    </div>
  </div>
);
